import { useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Lock, Sparkles, ExternalLink, Download, Search, X, Users, CalendarDays, Gauge, ArrowUp, ArrowDown, Brain, CheckCircle2, PencilLine, MessageSquare, ShoppingCart, Webhook, Settings, Trash2, Quote, UserCheck, UserX, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { GLOW } from "@/constants/testIds";

const isAbandonedLead = (l) => !((l?.cellulare || "").trim() || (l?.email || "").trim());

export default function Admin() {
  const [key, setKey] = useState("");
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [leadFilter, setLeadFilter] = useState("all");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [calib, setCalib] = useState(null);
  const [followups, setFollowups] = useState(null);
  const [sales, setSales] = useState(null);
  const [glowyLeads, setGlowyLeads] = useState(null);
  const [glowyFaqs, setGlowyFaqs] = useState(null);
  const [testimonials, setTestimonials] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [section, setSection] = useState("contatti");

  const auth = { headers: { "x-admin-key": key } };

  const loadReports = useCallback(async (k) => {
    const { data } = await api.get("/admin/reports", { headers: { "x-admin-key": k } });
    setReports(data.reports);
  }, []);

  const loadCalib = useCallback(async (k) => {
    try {
      const { data } = await api.get("/admin/calibrations", { headers: { "x-admin-key": k } });
      setCalib(data);
    } catch (e) { console.warn("Caricamento dati admin fallito:", e); }
  }, []);

  const loadFollowups = useCallback(async (k) => {
    try {
      const { data } = await api.get("/admin/followups", { headers: { "x-admin-key": k } });
      setFollowups(data);
    } catch (e) { console.warn("Caricamento dati admin fallito:", e); }
  }, []);

  const loadSales = useCallback(async (k) => {
    try {
      const { data } = await api.get("/admin/sales", { headers: { "x-admin-key": k } });
      setSales(data);
    } catch (e) { console.warn("Caricamento dati admin fallito:", e); }
  }, []);

  const loadGlowyLeads = useCallback(async (k) => {
    try {
      const { data } = await api.get("/admin/glowy-leads", { headers: { "x-admin-key": k } });
      setGlowyLeads(data);
    } catch (e) { console.warn("Caricamento dati admin fallito:", e); }
  }, []);

  const loadGlowyFaqs = useCallback(async (k) => {
    try {
      const { data } = await api.get("/admin/glowy-faqs", { headers: { "x-admin-key": k } });
      setGlowyFaqs(data);
    } catch (e) { console.warn("Caricamento dati admin fallito:", e); }
  }, []);

  const loadTestimonials = useCallback(async (k) => {
    try {
      const { data } = await api.get("/admin/testimonials", { headers: { "x-admin-key": k } });
      setTestimonials(data);
    } catch (e) { console.warn("Caricamento dati admin fallito:", e); }
  }, []);

  const login = async () => {
    setLoading(true);
    try {
      await loadReports(key);
      await loadCalib(key);
      await loadFollowups(key);
      await loadSales(key);
      await loadGlowyLeads(key);
      await loadGlowyFaqs(key);
      await loadTestimonials(key);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Accesso negato");
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    if (!reports) return [];
    const q = query.trim().toLowerCase();
    return reports.filter((r) => {
      const l = r.lead || {};
      if (q) {
        const hay = `${l.nome || ""} ${l.cognome || ""} ${l.cellulare || ""} ${l.email || ""} ${l.focus_concern || ""} ${r.report?.concern_focus?.concern || ""} ${r.n_analisi || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (leadFilter === "abandoned" && !isAbandonedLead(l)) return false;
      if (leadFilter === "completed" && isAbandonedLead(l)) return false;
      const day = (r.created_at || "").slice(0, 10);
      if (dateFrom && day < dateFrom) return false;
      if (dateTo && day > dateTo) return false;
      return true;
    });
  }, [reports, query, dateFrom, dateTo, leadFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av, bv;
      if (sortKey === "glow") { av = a.report?.glow_score ?? -1; bv = b.report?.glow_score ?? -1; }
      else { av = a.created_at || ""; bv = b.created_at || ""; }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (k) => { if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortKey(k); setSortDir("desc"); } };
  const SortIcon = ({ col }) => sortKey !== col ? null : (sortDir === "asc" ? <ArrowUp className="inline h-3 w-3 ml-1" /> : <ArrowDown className="inline h-3 w-3 ml-1" />);
  const clearFilters = () => { setQuery(""); setDateFrom(""); setDateTo(""); setLeadFilter("all"); };

  const stats = useMemo(() => {
    const all = reports || [];
    const today = new Date().toISOString().slice(0, 10);
    const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
    const glows = all.map((r) => r.report?.glow_score).filter((g) => typeof g === "number");
    const abandoned = all.filter((r) => isAbandonedLead(r.lead || {})).length;
    const completed = all.length - abandoned;
    return {
      total: all.length,
      today: all.filter((r) => (r.created_at || "").slice(0, 10) === today).length,
      week: all.filter((r) => (r.created_at || "").slice(0, 10) >= weekAgo).length,
      avgGlow: glows.length ? Math.round(glows.reduce((a, b) => a + b, 0) / glows.length) : "—",
      completed,
      abandoned,
      dropoff: all.length ? Math.round((abandoned / all.length) * 100) : 0,
    };
  }, [reports]);

  const daily = useMemo(() => {
    const map = {};
    (reports || []).forEach((r) => {
      const day = (r.created_at || "").slice(0, 10);
      if (!day) return;
      if (!map[day]) map[day] = { day, total: 0, abandoned: 0 };
      map[day].total += 1;
      if (isAbandonedLead(r.lead || {})) map[day].abandoned += 1;
    });
    return Object.values(map).sort((a, b) => (a.day < b.day ? -1 : 1)).slice(-14);
  }, [reports]);

  const glowDist = useMemo(() => {
    const buckets = [
      { label: "45-54", min: 45, max: 54, n: 0 },
      { label: "55-64", min: 55, max: 64, n: 0 },
      { label: "65-74", min: 65, max: 74, n: 0 },
      { label: "75-84", min: 75, max: 84, n: 0 },
      { label: "85-95", min: 85, max: 95, n: 0 },
    ];
    let has78 = 0, total = 0;
    (reports || []).forEach((r) => {
      const g = r.report?.glow_score;
      if (typeof g !== "number") return;
      total += 1;
      if (g === 78) has78 += 1;
      const b = buckets.find((x) => g >= x.min && g <= x.max) || buckets[buckets.length - 1];
      b.n += 1;
    });
    return { buckets, total, pct78: total ? Math.round((has78 / total) * 100) : 0 };
  }, [reports]);

  const exportCsv = () => {
    const rows = [["N Analisi", "Nome", "Cognome", "Cellulare", "Email", "Cruccio", "Glow Score", "Revisionato", "Data"]];
    filtered.forEach((r) => {
      const l = r.lead || {};
      const cruccio = l.focus_concern || r.report?.concern_focus?.concern || "";
      rows.push([r.n_analisi || "", l.nome || "", l.cognome || "", l.cellulare || "", l.email || "", cruccio,
        r.report?.glow_score ?? "", r.review ? "Sì" : "No", new Date(r.created_at).toLocaleString("it-IT")]);
    });
    const csv = rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `skinlovers-lead-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const submitReview = async (form) => {
    try {
      await api.post(`/admin/reports/${reviewTarget.id}/review`, form, auth);
      toast.success("Revisione salvata");
      setReviewTarget(null);
      await loadReports(key);
      await loadCalib(key);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore nel salvataggio"); }
  };

  const generateCalib = async () => {
    toast.info("Genero la calibrazione…");
    try {
      const { data } = await api.post("/admin/calibrations/generate", {}, auth);
      toast.success(data.already_proposed ? "Esiste già una versione proposta" : "Calibrazione generata");
      await loadCalib(key);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const approveCalib = async (version) => {
    try {
      await api.post(`/admin/calibrations/${version}/approve`, {}, auth);
      toast.success(`Versione v${version} attivata`);
      await loadCalib(key);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const addFaq = async (question, answer) => {
    try {
      await api.post("/admin/glowy-faqs", { question, answer }, auth);
      toast.success("FAQ aggiunta");
      await loadGlowyFaqs(key);
      return true;
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); return false; }
  };

  const deleteFaq = async (id) => {
    try {
      await api.delete(`/admin/glowy-faqs/${id}`, auth);
      await loadGlowyFaqs(key);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const toggleFollowups = async (enabled) => {
    try {
      await api.post("/admin/followups/settings", { enabled }, auth);
      toast.success(enabled ? "Invio automatico attivato" : "Invio automatico spento");
      await loadFollowups(key);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const sendFollowupNow = async (id) => {
    try {
      await api.post(`/admin/followups/${id}/send-now`, {}, auth);
      toast.success("Messaggio inviato ✓");
      await loadFollowups(key);
    } catch (err) { toast.error(err?.response?.data?.detail || "Invio non riuscito"); }
  };

  const testFollowup = async (channel, to) => {
    try {
      await api.post("/admin/followups/test", { channel, to }, auth);
      toast.success("Test inviato ✓ controlla WhatsApp/email");
    } catch (err) { toast.error(err?.response?.data?.detail || "Test non riuscito"); }
  };

  const saveWaTemplates = async (tpls) => {
    try {
      const templates = Object.fromEntries(Object.entries(tpls).map(([k, v]) => [k, { name: v?.name || "", lang: "it", webhook: v?.webhook || "" }]));
      await api.post("/admin/followups/wa-templates", { templates }, auth);
      toast.success("Configurazione salvata");
      await loadFollowups(key);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const addTestimonial = async (nome, commento) => {
    try {
      await api.post("/admin/testimonials", { nome, commento }, auth);
      toast.success("Recensione aggiunta");
      await loadTestimonials(key);
      return true;
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); return false; }
  };

  const deleteTestimonial = async (id) => {
    try {
      await api.delete(`/admin/testimonials/${id}`, auth);
      await loadTestimonials(key);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const registerWebhook = async () => {    toast.info("Registro il webhook ordini…");
    try {
      const { data } = await api.post("/admin/shopify/register-webhook", {}, auth);
      toast.success("Webhook ordini registrato ✓");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore registrazione webhook"); }
  };

  const resetDemo = async () => {
    const code = window.prompt("Per azzerare i dati inserisci il CODICE DI ACCESSO originale:");
    if (!code) return;
    if (!window.confirm("Sei sicuro? Verranno azzerati: analisi, ordini/ricavi, click, follow-up e contatti Glowy. FAQ e calibrazione AI NON verranno toccate. Operazione irreversibile.")) return;
    toast.info("Azzero i dati…");
    try {
      const { data } = await api.post("/admin/reset-demo", {}, { headers: { "x-admin-key": code } });
      toast.success("Dati azzerati ✓");
      await loadReports(key);
      await loadSales(key);
      await loadFollowups(key);
      await loadGlowyLeads(key);
    } catch (err) { toast.error(err?.response?.status === 401 ? "Codice di accesso errato." : (err?.response?.data?.detail || "Errore durante l'azzeramento")); }
  };

  const filterInput = "rounded-xl border border-[#E8E6E1] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#C17F59]";
  const hasFilters = query || dateFrom || dateTo || leadFilter !== "all";

  if (reports === null) {
    return (
      <div className="relative z-10 mx-auto max-w-md px-5 py-16">
        <div className="flex items-center gap-2 mb-8"><Sparkles className="h-5 w-5 text-[#C17F59]" /><span className="text-xs uppercase tracking-[0.3em] text-[#7A7571]">Skin Lovers · Admin</span></div>
        <div className="rounded-3xl bg-white p-8" style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.08)" }}>
          <div className="flex items-center gap-2 mb-6 text-[#C17F59]"><Lock className="h-5 w-5" /><h1 className="font-display text-2xl text-[#2D2825]">Area riservata</h1></div>
          <input data-testid={GLOW.adminKeyInput} type="password" value={key} onChange={(e) => setKey(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()} placeholder="Chiave admin"
            className="w-full rounded-xl border border-[#E8E6E1] bg-[#FAF9F6] px-4 py-3 outline-none focus:ring-2 focus:ring-[#C17F59]" />
          <button data-testid={GLOW.adminLoginButton} onClick={login} disabled={loading || !key}
            className="mt-4 w-full rounded-full bg-[#C17F59] px-6 py-3 text-white font-medium hover:bg-[#a96a47] disabled:opacity-40">{loading ? "Accesso…" : "Entra"}</button>
        </div>
      </div>
    );
  }

  const NAV = [
    { key: "contatti", label: "Contatti", icon: Users },
    { key: "vendite", label: "Vendite & Funnel", icon: ShoppingCart },
    { key: "followup", label: "Follow-up", icon: MessageSquare },
    { key: "calibrazione", label: "Calibrazione AI", icon: Brain },
    { key: "glowy-contatti", label: "Contatti Glowy", icon: MessageSquare },
    { key: "glowy-faq", label: "FAQ Glowy", icon: PencilLine },
    { key: "recensioni", label: "Recensioni", icon: Quote },
    { key: "impostazioni", label: "Impostazioni", icon: Settings },
  ];

  const ContattiSection = (
    <div data-testid={GLOW.adminReportsList}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="font-display text-3xl text-[#2D2825]">Contatti raccolti ({filtered.length})</h1>
        <button data-testid="export-csv-button" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full bg-[#2D2825] px-5 py-2.5 text-sm text-white hover:bg-black"><Download className="h-4 w-4" /> Esporta CSV</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4" data-testid="lead-stats">
        <StatCard icon={<Users className="h-5 w-5" />} label="Scan avviati" value={stats.total} />
        <StatCard icon={<CalendarDays className="h-5 w-5" />} label="Oggi" value={stats.today} />
        <StatCard icon={<CalendarDays className="h-5 w-5" />} label="Ultimi 7 giorni" value={stats.week} />
        <StatCard icon={<Gauge className="h-5 w-5" />} label="Glow medio" value={stats.avgGlow} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="funnel-stats">
        <StatCard icon={<UserCheck className="h-5 w-5" />} label="Contatti lasciati" value={stats.completed} />
        <StatCard icon={<UserX className="h-5 w-5" />} label="Abbandonati al gate" value={stats.abandoned} />
        <StatCard icon={<TrendingDown className="h-5 w-5" />} label="Drop-off al gate" value={`${stats.dropoff}%`} />
      </div>

      <DropoffChart daily={daily} />
      <GlowDistChart dist={glowDist} />

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7571]" />
          <input data-testid="lead-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca nome, cognome, cellulare, email, cruccio…" className={`${filterInput} w-full pl-9`} />
        </div>
        <label className="flex items-center gap-2 text-sm text-[#7A7571]">Dal<input data-testid="lead-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={filterInput} /></label>
        <label className="flex items-center gap-2 text-sm text-[#7A7571]">Al<input data-testid="lead-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={filterInput} /></label>
        <div className="flex items-center gap-1 rounded-full bg-[#F0EEEA] p-1" data-testid="lead-filter">
          {[["all", "Tutti"], ["completed", "Completati"], ["abandoned", "Abbandonati"]].map(([v, t]) => (
            <button key={v} data-testid={`lead-filter-${v}`} onClick={() => setLeadFilter(v)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${leadFilter === v ? "bg-white text-[#2D2825] shadow-sm" : "text-[#7A7571] hover:text-[#2D2825]"}`}>{t}</button>
          ))}
        </div>
        {hasFilters && <button data-testid="clear-filters" onClick={clearFilters} className="inline-flex items-center gap-1 text-sm text-[#C17F59] hover:underline"><X className="h-3.5 w-3.5" /> Azzera</button>}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-[#E8E6E1] bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#7A7571] border-b border-[#E8E6E1]">
              <th className="px-4 py-3">N° Analisi</th><th className="px-4 py-3">Nome</th><th className="px-4 py-3">Cognome</th>
              <th className="px-4 py-3">Cellulare</th><th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Cruccio</th>
              <th className="px-4 py-3 cursor-pointer select-none hover:text-[#C17F59]" data-testid="sort-glow" onClick={() => toggleSort("glow")}>Glow<SortIcon col="glow" /></th>
              <th className="px-4 py-3 cursor-pointer select-none hover:text-[#C17F59]" data-testid="sort-date" onClick={() => toggleSort("created_at")}>Data<SortIcon col="created_at" /></th>
              <th className="px-4 py-3">Revisione</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const l = r.lead || {};
              return (
                <tr key={r.id} className="border-b border-[#F0EEEA] hover:bg-[#FAF9F6]">
                  <td className="px-4 py-3 font-mono text-[#C17F59]">{r.n_analisi || "—"}</td>
                  <td className="px-4 py-3 text-[#2D2825]">
                    <div className="flex items-center gap-2">
                      <span>{l.nome || "—"}</span>
                      {isAbandonedLead(l) && <span data-testid={`lead-abandoned-${r.n_analisi}`} className="inline-block rounded-full bg-[#FBE9E7] text-[#C0392B] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">Abbandonato</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#2D2825]">{l.cognome || "—"}</td>
                  <td className="px-4 py-3">{l.cellulare || "—"}</td>
                  <td className="px-4 py-3">{l.email || "—"}</td>
                  <td className="px-4 py-3 max-w-[220px]" data-testid={`lead-cruccio-${r.n_analisi}`}>
                    {(l.focus_concern || r.report?.concern_focus?.concern)
                      ? <span className="inline-block rounded-full bg-[#F6EFE9] text-[#8a5a3c] px-2.5 py-1 text-xs font-medium">{l.focus_concern || r.report?.concern_focus?.concern}{!l.focus_concern && r.report?.concern_focus?.concern ? " ·AI" : ""}</span>
                      : <span className="text-[#B8B4AF]">—</span>}
                  </td>
                  <td className="px-4 py-3 font-medium">{r.report?.glow_score ?? (r.status === "processing" ? "…" : "—")}</td>
                  <td className="px-4 py-3 text-[#7A7571]">{new Date(r.created_at).toLocaleDateString("it-IT")}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {(r.reviews?.length || r.review) ? <span className="inline-flex items-center gap-1 text-[#5f7d5c] text-xs"><CheckCircle2 className="h-4 w-4" /> {r.reviews?.length || 1}</span> : null}
                      <button data-testid={`review-btn-${r.n_analisi}`} onClick={() => setReviewTarget(r)} disabled={r.status !== "done"} className="inline-flex items-center gap-1 text-[#C17F59] text-xs hover:underline disabled:opacity-40"><PencilLine className="h-3.5 w-3.5" /> {(r.reviews?.length || r.review) ? "Aggiungi" : "Revisiona"}</button>
                    </div>
                  </td>
                  <td className="px-4 py-3"><Link to={`/r/${r.id}`} className="inline-flex items-center gap-1 text-[#C17F59] hover:underline">Apri <ExternalLink className="h-3.5 w-3.5" /></Link></td>
                </tr>
              );
            })}
            {sorted.length === 0 && <tr><td colSpan={10} className="px-4 py-6 text-center text-[#7A7571]">Nessun contatto trovato.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderSection = () => {
    switch (section) {
      case "vendite": return sales ? <SalesPanel data={sales} onRegister={registerWebhook} /> : null;
      case "followup": return followups ? <FollowupPanel data={followups} onToggle={toggleFollowups} onSendNow={sendFollowupNow} onTest={testFollowup} onSaveTemplates={saveWaTemplates} /> : null;
      case "calibrazione": return calib ? <CalibrationPanel calib={calib} onGenerate={generateCalib} onApprove={approveCalib} /> : null;
      case "glowy-contatti": return glowyLeads ? <GlowyLeadsPanel data={glowyLeads} /> : null;
      case "glowy-faq": return glowyFaqs ? <GlowyFaqPanel data={glowyFaqs} onAdd={addFaq} onDelete={deleteFaq} /> : null;
      case "recensioni": return <TestimonialsPanel data={testimonials} onAdd={addTestimonial} onDelete={deleteTestimonial} />;
      case "impostazioni": return <SettingsPanel onReset={resetDemo} />;
      default: return ContattiSection;
    }
  };

  return (
    <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 py-8">
      <div className="flex items-center gap-2 mb-6"><Sparkles className="h-5 w-5 text-[#C17F59]" /><span className="text-xs uppercase tracking-[0.3em] text-[#7A7571]">Skin Lovers · Super Admin</span></div>
      <div className="flex flex-col md:flex-row gap-6">
        {/* Menu */}
        <aside className="md:w-60 md:shrink-0">
          <nav data-testid="admin-menu" className="flex md:flex-col gap-1.5 overflow-x-auto md:overflow-visible rounded-2xl bg-white border border-[#E8E6E1] p-2" style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.05)" }}>
            {NAV.map((n) => {
              const Icon = n.icon;
              const active = section === n.key;
              return (
                <button key={n.key} data-testid={`admin-nav-${n.key}`} onClick={() => setSection(n.key)}
                  className={`flex items-center gap-2.5 whitespace-nowrap rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${active ? "bg-[#C17F59] text-white" : "text-[#2D2825] hover:bg-[#FAF9F6]"}`}>
                  <Icon className="h-4 w-4 shrink-0" /> {n.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Contenuto sezione */}
        <main className="flex-1 min-w-0">
          {renderSection()}
        </main>
      </div>

      {reviewTarget && <ReviewModal report={reviewTarget} onClose={() => setReviewTarget(null)} onSubmit={submitReview} />}
    </div>
  );
}

const SettingsPanel = ({ onReset }) => (
  <div data-testid="settings-panel" className="rounded-3xl bg-white p-7" style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.06)" }}>
    <div className="flex items-center gap-2 mb-1"><Settings className="h-5 w-5 text-[#C17F59]" /><h2 className="font-display text-2xl text-[#2D2825]">Impostazioni</h2></div>
    <p className="text-sm text-[#7A7571] mb-6">Strumenti di gestione dell'area amministrativa.</p>

    <div className="rounded-2xl border-2 border-dashed border-[#C87979]/40 bg-[#C87979]/5 p-5">
      <h3 className="font-display text-lg text-[#2D2825] flex items-center gap-2"><Trash2 className="h-4 w-4 text-[#C87979]" /> Azzera dati demo</h3>
      <p className="mt-1 text-sm text-[#7A7571]">Cancella <strong>analisi, ordini/ricavi, click, follow-up e contatti Glowy</strong>. Non tocca le FAQ né la calibrazione AI. Utile per ripartire da zero prima del lancio. Operazione <strong>irreversibile</strong>.</p>
      <button data-testid="reset-demo-button" onClick={onReset}
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#C87979] px-5 py-2.5 text-sm text-white hover:bg-[#b56767]">
        <Trash2 className="h-4 w-4" /> Azzera ora
      </button>
    </div>
  </div>
);

const GlowDistChart = ({ dist }) => {
  if (!dist || dist.total === 0) return null;
  const max = Math.max(...dist.buckets.map((b) => b.n), 1);
  return (
    <div className="rounded-2xl bg-white border border-[#E8E6E1] p-5 mb-6" data-testid="glow-dist-chart" style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.05)" }}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="text-sm font-medium text-[#2D2825]">Distribuzione Glow Score</p>
          <p className="text-xs text-[#7A7571]">Verifica che i voti siano ben distribuiti e non concentrati.</p>
        </div>
        <div data-testid="glow-pct78" className={`rounded-full px-3 py-1.5 text-xs font-semibold ${dist.pct78 > 15 ? "bg-[#FBE9E7] text-[#C0392B]" : "bg-[#8BA888]/15 text-[#5f7d5c]"}`}>
          Voti = 78 esatti: {dist.pct78}% {dist.pct78 > 15 ? "⚠️ troppi" : "✓ ok"}
        </div>
      </div>
      <div className="flex items-end gap-3 h-40">
        {dist.buckets.map((b) => {
          const h = Math.round((b.n / max) * 120) + 4;
          const pct = dist.total ? Math.round((b.n / dist.total) * 100) : 0;
          return (
            <div key={b.label} className="flex flex-col items-center gap-1.5 flex-1" data-testid={`glow-bucket-${b.label}`} title={`${b.label}: ${b.n} (${pct}%)`}>
              <span className="text-[11px] font-semibold text-[#2D2825]">{b.n}</span>
              <div className="w-full rounded-t-md bg-gradient-to-t from-[#C17F59] to-[#d9a179]" style={{ height: h }} />
              <span className="text-[10px] text-[#7A7571]">{b.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value }) => (
  <div className="rounded-2xl bg-white border border-[#E8E6E1] p-5" style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.05)" }}>
    <div className="flex items-center gap-2 text-[#C17F59] mb-2">{icon}<span className="text-[11px] uppercase tracking-[0.15em] text-[#7A7571]">{label}</span></div>
    <p className="font-display text-3xl text-[#2D2825]">{value}</p>
  </div>
);

const DropoffChart = ({ daily }) => {
  if (!daily || daily.length === 0) return null;
  const maxTotal = Math.max(...daily.map((d) => d.total), 1);
  const fmtDay = (iso) => { const [, m, dd] = iso.split("-"); return `${dd}/${m}`; };
  return (
    <div className="rounded-2xl bg-white border border-[#E8E6E1] p-5 mb-6" data-testid="dropoff-chart" style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.05)" }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-medium text-[#2D2825]">Drop-off al gate — per giorno</p>
          <p className="text-xs text-[#7A7571]">Ogni barra è un giorno: <span className="text-[#8BA888]">verde</span> = ha lasciato i contatti · <span className="text-[#C0392B]">rosso</span> = abbandonato al gate.</p>
        </div>
        <div className="hidden sm:flex items-center gap-3 text-[11px] text-[#7A7571]">
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-[#8BA888]" /> Completati</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-[#C0392B]" /> Abbandonati</span>
        </div>
      </div>
      <div className="flex items-end gap-2 h-44 overflow-x-auto pb-1">
        {daily.map((d) => {
          const h = Math.round((d.total / maxTotal) * 140) + 4;
          const abandPct = d.total ? (d.abandoned / d.total) * 100 : 0;
          const dropoff = d.total ? Math.round((d.abandoned / d.total) * 100) : 0;
          return (
            <div key={d.day} className="flex flex-col items-center gap-1.5 flex-shrink-0" style={{ width: 34 }} data-testid={`dropoff-bar-${d.day}`} title={`${fmtDay(d.day)} · ${d.total} scan · ${d.abandoned} abbandonati (${dropoff}%)`}>
              <span className="text-[10px] font-semibold text-[#C0392B]">{dropoff}%</span>
              <div className="w-full rounded-md overflow-hidden flex flex-col justify-end bg-[#F0EEEA]" style={{ height: h }}>
                <div className="w-full bg-[#C0392B]" style={{ height: `${abandPct}%` }} />
                <div className="w-full bg-[#8BA888]" style={{ height: `${100 - abandPct}%` }} />
              </div>
              <span className="text-[10px] text-[#7A7571]">{fmtDay(d.day)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CalibrationPanel = ({ calib, onGenerate, onApprove }) => {
  const cals = calib.calibrations || [];
  const active = cals.find((c) => c.status === "active");
  const proposed = cals.find((c) => c.status === "proposed");
  const base = (calib.next_at || 100) - 100;
  const progress = Math.max(0, Math.min(100, Math.round(((calib.reviewed_total - base) / 100) * 100)));
  return (
    <div data-testid="calib-panel" className="mb-8 rounded-3xl bg-[#2D2825] text-white p-7">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2"><Brain className="h-5 w-5 text-[#C17F59]" /><h2 className="font-display text-2xl">Calibrazione AI</h2></div>
        <button data-testid="calib-generate" onClick={onGenerate} className="rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 text-sm">Genera ora</button>
      </div>
      <p className="text-sm text-white/70 mb-2">Revisioni staff: <strong className="text-white">{calib.reviewed_total}</strong> · prossima calibrazione automatica a <strong className="text-white">{calib.next_at}</strong></p>
      <div className="h-2 rounded-full bg-white/15 overflow-hidden mb-5"><div className="h-full bg-[#C17F59]" style={{ width: `${progress}%` }} /></div>

      {active && (
        <div className="rounded-2xl bg-white/5 p-4 mb-3">
          <p className="text-xs uppercase tracking-[0.2em] text-[#8BA888]">Versione attiva · v{active.version}</p>
          <p className="text-sm text-white/85 mt-1">{active.summary}</p>
        </div>
      )}
      {proposed && (
        <div data-testid="calib-proposed" className="rounded-2xl bg-[#C17F59]/20 border border-[#C17F59]/40 p-4 mb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C17F59]">Proposta · v{proposed.version} · su {proposed.based_on_reviews} revisioni</p>
              <p className="text-sm text-white/90 mt-1">{proposed.summary}</p>
            </div>
            <button data-testid="calib-approve" onClick={() => onApprove(proposed.version)} className="rounded-full bg-white text-[#a96a47] px-5 py-2 text-sm font-medium hover:bg-white/90">Approva e attiva</button>
          </div>
        </div>
      )}
      {cals.length === 0 && <p className="text-sm text-white/50">Nessuna calibrazione ancora. La prima verrà proposta al raggiungimento di 100 revisioni.</p>}

      {cals.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-sm text-white/70 hover:text-white">Storico versioni ({cals.length})</summary>
          <div className="mt-3 space-y-2">
            {cals.map((c) => (
              <div key={c.version} className="flex items-center justify-between text-xs border-t border-white/10 pt-2">
                <span>v{c.version} · {c.status} · {new Date(c.created_at).toLocaleDateString("it-IT")} · {c.based_on_reviews} rev.</span>
                <span className="text-white/50 truncate max-w-[50%]">{c.summary}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

const ReviewModal = ({ report, onClose, onSubmit }) => {
  const history = report.reviews || (report.review ? [report.review] : []);
  const [glow, setGlow] = useState(report.review?.glow_score_corrected ?? report.report?.glow_score ?? "");
  const [add, setAdd] = useState("");
  const [remove, setRemove] = useState("");
  const [comment, setComment] = useState("");
  const cls = "w-full rounded-xl border border-[#E8E6E1] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#C17F59]";
  const submit = () => onSubmit({
    glow_score_corrected: glow === "" ? null : Number(glow),
    add_products: add || null, remove_products: remove || null,
    comment: comment || null, reviewed_by: "Valentina / Staff",
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div data-testid="review-modal" className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-white p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1"><h3 className="font-display text-2xl text-[#2D2825]">Revisione · {report.n_analisi}</h3><button onClick={onClose}><X className="h-5 w-5 text-[#7A7571]" /></button></div>
        <p className="text-sm text-[#7A7571] mb-5">Le tue correzioni e il commento vengono usati per calibrare l'AI. Ogni revisione viene conservata nello storico.</p>

        {history.length > 0 && (
          <div className="mb-5" data-testid="review-history">
            <p className="text-xs uppercase tracking-[0.15em] text-[#7A7571] mb-2">Revisioni precedenti ({history.length})</p>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {history.map((h, i) => (
                <div key={h.reviewed_at || i} className="rounded-xl bg-[#FAF9F6] border border-[#E8E6E1] p-3 text-xs">
                  <div className="flex justify-between text-[#7A7571]">
                    <span>{h.reviewed_by || "Staff"}</span>
                    <span>{h.reviewed_at ? new Date(h.reviewed_at).toLocaleString("it-IT") : ""}</span>
                  </div>
                  {h.glow_score_corrected != null && <p className="text-[#2D2825] mt-1">Glow corretto: <strong>{h.glow_score_corrected}</strong></p>}
                  {h.add_products && <p className="text-[#2D2825]">+ {h.add_products}</p>}
                  {h.remove_products && <p className="text-[#2D2825]">− {h.remove_products}</p>}
                  {h.comment && <p className="text-[#7A7571] italic mt-1">"{h.comment}"</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        <label className="block text-xs uppercase tracking-[0.15em] text-[#7A7571] mb-1">Glow Score corretto</label>
        <input data-testid="review-glow" type="number" min="0" max="100" value={glow} onChange={(e) => setGlow(e.target.value)} className={cls} />
        <label className="block text-xs uppercase tracking-[0.15em] text-[#7A7571] mb-1 mt-4">Prodotti da aggiungere</label>
        <input data-testid="review-add" value={add} onChange={(e) => setAdd(e.target.value)} placeholder="es. Siero Vitamina C, ..." className={cls} />
        <label className="block text-xs uppercase tracking-[0.15em] text-[#7A7571] mb-1 mt-4">Prodotti da togliere</label>
        <input data-testid="review-remove" value={remove} onChange={(e) => setRemove(e.target.value)} placeholder="es. ..." className={cls} />
        <label className="block text-xs uppercase tracking-[0.15em] text-[#7A7571] mb-1 mt-4">Commento di Valentina</label>
        <textarea data-testid="review-comment" rows={4} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Note, aggiustamenti, osservazioni…" className={cls} />
        <button data-testid="review-submit" onClick={submit} className="mt-5 w-full rounded-full bg-[#C17F59] px-6 py-3 text-white font-medium hover:bg-[#a96a47]">Salva revisione</button>
      </div>
    </div>
  );
};

const STATUS_LABEL = {
  scheduled: { t: "In coda", c: "bg-[#F6EFE9] text-[#8a5a3c]" },
  sent: { t: "Inviato", c: "bg-[#8BA888]/15 text-[#5f7d5c]" },
  failed: { t: "Errore", c: "bg-[#C87979]/15 text-[#a75454]" },
  cancelled_purchase: { t: "Annullato (ha acquistato)", c: "bg-[#E8E6E1] text-[#7A7571]" },
};

const FollowupPanel = ({ data, onToggle, onSendNow, onTest, onSaveTemplates }) => {
  const list = data.followups || [];
  const ghl = data.ghl || {};
  const configured = ghl.token && ghl.location_id;
  const [testChannel, setTestChannel] = useState("whatsapp");
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState("");
  const [tpls, setTpls] = useState(() => {
    const t = data.wa_templates || {};
    return Object.fromEntries(Object.entries(t).map(([k, v]) => [k, { name: v?.name || "", webhook: v?.webhook || "" }]));
  });
  const fmt = (d) => new Date(d).toLocaleString("it-IT");
  return (
    <div data-testid="followup-panel" className="mb-8 rounded-3xl bg-white border border-[#E8E6E1] p-7" style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.05)" }}>
      <div className="flex items-center gap-2 mb-1">
        <MessageSquare className="h-5 w-5 text-[#C17F59]" />
        <h2 className="font-display text-2xl text-[#2D2825]">Follow-up WhatsApp & Email</h2>
      </div>
      <p className="text-sm text-[#7A7571] mb-5">{data.scheduled} messaggi programmati. Invio tramite <strong>GoHighLevel</strong>, controllo automatico ogni 20 secondi.</p>

      {/* Stato connessione + interruttore */}
      <div className="rounded-2xl border border-[#E8E6E1] bg-[#FAF9F6] p-4 mb-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm">
            <p className="font-medium text-[#2D2825]">Connessione GoHighLevel</p>
            <p className="text-xs text-[#7A7571] mt-1" data-testid="ghl-config-state">
              Token {ghl.token ? "✓" : "✗"} · Location {ghl.location_id ? "✓" : "✗"} ·
              WhatsApp {ghl.whatsapp_from || "non impostato"} · Email {ghl.email_from || "non impostata"}
            </p>
          </div>
          <button data-testid="followup-toggle" disabled={!configured || busy === "toggle"}
            onClick={async () => { setBusy("toggle"); await onToggle(!data.sending_enabled); setBusy(""); }}
            className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-40 ${data.sending_enabled ? "bg-[#5f7d5c] text-white hover:bg-[#4e6a4c]" : "bg-[#2D2825] text-white hover:bg-black"}`}>
            {data.sending_enabled ? "Invio automatico: ATTIVO" : "Invio automatico: SPENTO"}
          </button>
        </div>
        {!configured && <p className="mt-3 text-xs text-[#a75454]">Inserisci GHL_TOKEN e GHL_LOCATION_ID nelle variabili d'ambiente del backend per attivare l'invio.</p>}
      </div>

      {/* Test reale */}
      <div className="rounded-2xl border border-[#E8E6E1] p-4 mb-6">
        <p className="text-sm font-medium text-[#2D2825] mb-3">Test reale (invia subito un messaggio a te)</p>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="test-channel" value={testChannel} onChange={(e) => setTestChannel(e.target.value)}
            className="rounded-xl border border-[#E8E6E1] bg-white px-3 py-2 text-sm">
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option>
          </select>
          <input data-testid="test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)}
            placeholder={testChannel === "whatsapp" ? "+39333…" : "tua@email.it"}
            className="flex-1 min-w-[200px] rounded-xl border border-[#E8E6E1] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#C17F59]" />
          <button data-testid="test-send" disabled={!configured || !testTo.trim() || busy === "test"}
            onClick={async () => { setBusy("test"); await onTest(testChannel, testTo.trim()); setBusy(""); }}
            className="rounded-full bg-[#C17F59] px-5 py-2 text-sm text-white hover:bg-[#a96a47] disabled:opacity-40">
            {busy === "test" ? "Invio…" : "Invia test"}
          </button>
        </div>
      </div>

      {/* Template WhatsApp approvati */}
      <div className="rounded-2xl border border-[#E8E6E1] p-4 mb-6" data-testid="wa-templates-box">
        <p className="text-sm font-medium text-[#2D2825]">Template WhatsApp — via Workflow GHL</p>
        <p className="text-xs text-[#7A7571] mt-1 mb-3">Per inviare i template <strong>fuori dalla finestra di 24h</strong>, l'unico metodo affidabile di GoHighLevel è un <strong>Workflow</strong>. Crea un Workflow con trigger <em>"Inbound Webhook"</em> + azione <em>"WhatsApp — Send Message"</em> (seleziona il template), poi incolla qui sotto l'URL del webhook per ogni fase. Se il campo Webhook è vuoto, il messaggio parte come testo libero (funziona solo entro 24h).</p>
        <div className="space-y-3">
          {[["reminder_1h", "Promemoria T+60s (nome, link)"],
            ["scadenza_20h", "Scadenza T+20h (nome, link)"],
            ["glowy_lead_1h", "Lead da Glowy T+1h (nome, link)"]].map(([tag, label]) => (
            <div key={tag} className="flex flex-wrap items-center gap-2">
              <span className="w-full sm:w-56 text-xs text-[#7A7571]">{label}</span>
              <input data-testid={`wa-template-${tag}`} value={tpls[tag]?.name || ""}
                onChange={(e) => setTpls((t) => ({ ...t, [tag]: { ...t[tag], name: e.target.value } }))}
                placeholder="nome_template (es. glow_reminder_1h)"
                className="flex-1 min-w-[150px] rounded-xl border border-[#E8E6E1] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#C17F59]" />
              <input data-testid={`wa-webhook-${tag}`} value={tpls[tag]?.webhook || ""}
                onChange={(e) => setTpls((t) => ({ ...t, [tag]: { ...t[tag], webhook: e.target.value } }))}
                placeholder="URL Webhook del Workflow GHL (https://…)"
                className="flex-1 min-w-[220px] rounded-xl border border-[#E8E6E1] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#C17F59]" />
            </div>
          ))}
        </div>
        <button data-testid="wa-templates-save" disabled={busy === "tpl"}
          onClick={async () => { setBusy("tpl"); await onSaveTemplates(tpls); setBusy(""); }}
          className="mt-3 rounded-full bg-[#2D2825] px-5 py-2 text-sm text-white hover:bg-black disabled:opacity-40">
          {busy === "tpl" ? "Salvo…" : "Salva template"}
        </button>
      </div>

      {list.length === 0 ? (
        <p className="text-sm text-[#7A7571]">Nessun follow-up in coda. Verranno generati automaticamente a ogni nuova analisi.</p>
      ) : (
        <details open>
          <summary className="cursor-pointer text-sm text-[#C17F59] hover:underline">Coda messaggi ({list.length})</summary>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[#7A7571] border-b border-[#E8E6E1]">
                  <th className="px-3 py-2">Quando</th><th className="px-3 py-2">Canale</th><th className="px-3 py-2">Fase</th>
                  <th className="px-3 py-2">A</th><th className="px-3 py-2">Stato</th><th className="px-3 py-2">Messaggio</th><th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {list.slice(0, 80).map((f) => {
                  const st = STATUS_LABEL[f.status] || { t: f.status, c: "bg-[#E8E6E1] text-[#7A7571]" };
                  return (
                    <tr key={f.id} data-testid="followup-row" className="border-b border-[#F0EEEA]">
                      <td className="px-3 py-2 whitespace-nowrap text-[#7A7571]">{fmt(f.scheduled_at)}</td>
                      <td className="px-3 py-2 capitalize">{f.channel}</td>
                      <td className="px-3 py-2">{f.tag}</td>
                      <td className="px-3 py-2">{f.to || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block rounded-full px-2 py-0.5 ${st.c}`}>{st.t}</span>
                        {f.error && <p className="mt-1 text-[10px] text-[#a75454] max-w-[180px]">{f.error}</p>}
                      </td>
                      <td className="px-3 py-2 text-[#7A7571] max-w-[280px] truncate">{f.message}</td>
                      <td className="px-3 py-2">
                        {f.status !== "sent" && f.status !== "cancelled_purchase" && (
                          <button data-testid={`send-now-${f.id}`} disabled={!configured || busy === f.id}
                            onClick={async () => { setBusy(f.id); await onSendNow(f.id); setBusy(""); }}
                            className="whitespace-nowrap rounded-full bg-[#2D2825] px-3 py-1.5 text-white hover:bg-black disabled:opacity-40">
                            {busy === f.id ? "…" : "Invia ora"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
};


const FunnelChart = ({ f, orders }) => {
  const analyses = f.total_analyses ?? 0;
  const clicks = f.total_clicks ?? 0;
  const sales = orders ?? 0;
  const maxVal = Math.max(analyses, clicks, sales, 1);
  const stages = [
    { key: "analyses", label: "Analisi completate", value: analyses, color: "#8BA888" },
    { key: "clicks", label: "Click al carrello", value: clicks, color: "#D4AF37", pct: f.cr_clicks_analyses ?? 0, from: "delle analisi" },
    { key: "sales", label: "Vendite reali", value: sales, color: "#C17F59", pct: f.cr_orders_clicks ?? 0, from: "dei click" },
  ];
  return (
    <div data-testid="funnel-chart" className="mb-6 rounded-2xl bg-white/5 p-5">
      <p className="text-[11px] uppercase tracking-[0.2em] text-white/60 mb-4">Imbuto di conversione</p>
      <div className="space-y-2">
        {stages.map((s, i) => {
          const width = Math.max((s.value / maxVal) * 100, 14);
          return (
            <div key={s.key}>
              {i > 0 && (
                <div className="flex items-center justify-center py-1.5" data-testid={`funnel-step-${s.key}`}>
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1 text-[11px] text-white/80">
                    <ArrowDown className="h-3 w-3" /> {s.pct}% {s.from}
                  </span>
                </div>
              )}
              <div className="flex justify-center">
                <div
                  className="relative flex items-center justify-between rounded-xl px-4 py-3 transition-all duration-500"
                  style={{ width: `${width}%`, minWidth: 180, background: `linear-gradient(90deg, ${s.color}, ${s.color}cc)` }}
                  data-testid={`funnel-bar-${s.key}`}
                >
                  <span className="text-sm font-medium text-[#1f3d33]">{s.label}</span>
                  <span className="font-display text-2xl text-[#1f3d33]">{s.value}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-center text-xs text-white/60">
        Conversione complessiva <strong className="text-white">analisi → vendita: {f.cr_orders_analyses ?? 0}%</strong>
      </p>
    </div>
  );
};

const PackageSales = ({ pkg, f }) => {
  const p = pkg || {};
  const cards = [
    { key: "base", label: "Carrello Base", clicks: f?.clicks_base ?? 0, color: "#C17F59", pctLabel: "−15%" },
    { key: "premium", label: "Carrello Premium", clicks: f?.clicks_premium ?? 0, color: "#8BA888", pctLabel: "−20%" },
    { key: "single", label: "Prodotto singolo", clicks: f?.clicks_single ?? 0, color: "#D4AF37", pctLabel: "−10%" },
  ];
  return (
    <div className="mt-6" data-testid="package-sales">
      <p className="text-[11px] uppercase tracking-[0.2em] text-white/60 mb-3">Vendite per pacchetto</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cards.map((c) => {
          const d = p[c.key] || { orders: 0, revenue: 0 };
          const conv = c.clicks ? Math.round((d.orders / c.clicks) * 100) : 0;
          return (
            <div key={c.key} data-testid={`pkg-${c.key}`} className="rounded-2xl bg-white/5 p-4 border-l-4" style={{ borderColor: c.color }}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-white">{c.label}</p>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: `${c.color}33`, color: c.color }}>{c.pctLabel}</span>
              </div>
              <div className="mt-3 flex items-end justify-between">
                <div>
                  <p className="font-display text-3xl text-white" data-testid={`pkg-${c.key}-orders`}>{d.orders}</p>
                  <p className="text-[11px] text-white/50">vendite</p>
                </div>
                <div className="text-right">
                  <p className="font-display text-xl text-white" data-testid={`pkg-${c.key}-revenue`}>€ {d.revenue}</p>
                  <p className="text-[11px] text-white/50">ricavi</p>
                </div>
              </div>
              <p className="mt-2 text-[11px] text-white/50">{c.clicks} click · conversione {conv}%</p>
            </div>
          );
        })}
      </div>
      {(p.other?.orders ?? 0) > 0 && (
        <p className="mt-2 text-xs text-white/50" data-testid="pkg-other">+ {p.other.orders} vendite senza codice Glow Score (€ {p.other.revenue}) — non attribuite a un pacchetto.</p>
      )}
    </div>
  );
};

const GlowyFaqPanel = ({ data, onAdd, onDelete }) => {
  const faqs = data?.faqs || [];
  const [q, setQ] = useState("");
  const [a, setA] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!q.trim() || !a.trim() || busy) return;
    setBusy(true);
    const ok = await onAdd(q.trim(), a.trim());
    setBusy(false);
    if (ok) { setQ(""); setA(""); }
  };
  return (
    <div data-testid="glowy-faq-panel" className="mb-8 rounded-3xl bg-white p-7" style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.06)" }}>
      <div className="flex items-center gap-2 mb-1">
        <PencilLine className="h-5 w-5 text-[#C17F59]" />
        <h2 className="font-display text-2xl text-[#2D2825]">FAQ di Glowy ({faqs.length})</h2>
      </div>
      <p className="text-sm text-[#7A7571] mb-4">Scrivi qui le domande frequenti con le tue risposte: Glowy le userà come fonte prioritaria quando risponde ai clienti.</p>

      <div className="rounded-2xl border border-[#E8E6E1] bg-[#FAF9F6] p-4 mb-5 space-y-3">
        <input data-testid="faq-question-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Domanda (es. Posso usarli in gravidanza?)"
          className="w-full rounded-xl border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#C17F59]" />
        <textarea data-testid="faq-answer-input" value={a} onChange={(e) => setA(e.target.value)} rows={2} placeholder="La tua risposta…"
          className="w-full resize-y rounded-xl border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#C17F59]" />
        <button data-testid="faq-add-button" onClick={submit} disabled={busy || !q.trim() || !a.trim()}
          className="rounded-full bg-[#C17F59] px-5 py-2.5 text-sm text-white hover:bg-[#a96a47] disabled:opacity-40">{busy ? "Salvo…" : "Aggiungi FAQ"}</button>
      </div>

      {faqs.length === 0 ? (
        <p className="text-sm text-[#B9B4AE]">Nessuna FAQ ancora. Aggiungi la prima qui sopra.</p>
      ) : (
        <div className="space-y-3">
          {faqs.map((f) => (
            <div key={f.id} data-testid="faq-item" className="rounded-2xl border border-[#E8E6E1] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-[#2D2825]">{f.question}</p>
                  <p className="mt-1 text-sm text-[#7A7571] whitespace-pre-wrap">{f.answer}</p>
                </div>
                <button data-testid="faq-delete-button" onClick={() => onDelete(f.id)}
                  className="shrink-0 rounded-full p-2 text-[#C87979] hover:bg-[#C87979]/10" aria-label="Elimina FAQ">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TestimonialsPanel = ({ data, onAdd, onDelete }) => {
  const items = data?.testimonials || [];
  const [nome, setNome] = useState("");
  const [commento, setCommento] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const submit = async () => {
    if (!nome.trim() || !commento.trim() || busy) return;
    setBusy(true);
    const ok = await onAdd(nome.trim(), commento.trim());
    setBusy(false);
    if (ok) { setNome(""); setCommento(""); }
  };
  const shown = q.trim()
    ? items.filter((t) => `${t.nome} ${t.commento}`.toLowerCase().includes(q.trim().toLowerCase()))
    : items;
  return (
    <div data-testid="testimonials-panel" className="mb-8 rounded-3xl bg-white p-7" style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.06)" }}>
      <div className="flex items-center gap-2 mb-1">
        <Quote className="h-5 w-5 text-[#C17F59]" />
        <h2 className="font-display text-2xl text-[#2D2825]">Recensioni clienti ({items.length})</h2>
      </div>
      <p className="text-sm text-[#7A7571] mb-4">Compaiono a rotazione (ordine casuale, cambio ogni 5 secondi) durante la scansione del viso e l'attesa dei carrelli. Se non ce n'è nessuna, non viene mostrato nulla.</p>

      <div className="rounded-2xl border border-[#E8E6E1] bg-[#FAF9F6] p-4 mb-5 space-y-3">
        <input data-testid="testimonial-nome-input" value={nome} onChange={(e) => setNome(e.target.value)}
          placeholder="Nome (es. Giulia, 42 anni – Bari)"
          className="w-full rounded-xl border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#C17F59]" />
        <textarea data-testid="testimonial-commento-input" value={commento} onChange={(e) => setCommento(e.target.value)} rows={3}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
          placeholder="Il commento della cliente…"
          className="w-full resize-y rounded-xl border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#C17F59]" />
        <button data-testid="testimonial-add-button" onClick={submit} disabled={busy || !nome.trim() || !commento.trim()}
          className="rounded-full bg-[#C17F59] px-5 py-2.5 text-sm text-white hover:bg-[#a96a47] disabled:opacity-40">{busy ? "Salvo…" : "Aggiungi recensione"}</button>
      </div>

      {items.length > 0 && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7571]" />
          <input data-testid="testimonial-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca fra le recensioni…"
            className="w-full rounded-xl border border-[#E8E6E1] bg-white pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#C17F59]" />
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-[#B9B4AE]">Nessuna recensione. Aggiungi la prima qui sopra: finché la lista è vuota, ai clienti non viene mostrato nulla.</p>
      ) : (
        <div className="space-y-3">
          {shown.map((t) => (
            <div key={t.id} data-testid="testimonial-item" className="rounded-2xl border border-[#E8E6E1] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-[#2D2825] whitespace-pre-wrap">“{t.commento}”</p>
                  <p className="mt-1.5 text-xs uppercase tracking-[0.15em] text-[#C17F59]">{t.nome}</p>
                </div>
                <button data-testid="testimonial-delete-button" onClick={() => onDelete(t.id)}
                  className="shrink-0 rounded-full p-2 text-[#C87979] hover:bg-[#C87979]/10" aria-label="Elimina recensione">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          {shown.length === 0 && <p className="text-sm text-[#B9B4AE]">Nessuna recensione corrisponde alla ricerca.</p>}
        </div>
      )}
    </div>
  );
};

const GlowyLeadsPanel = ({ data }) => {
  const leads = data?.leads || [];
  const waLink = (n) => `https://wa.me/${String(n).replace(/[^0-9]/g, "")}`;
  return (
    <div data-testid="glowy-leads-panel" className="mb-8 rounded-3xl bg-white p-7" style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.06)" }}>
      <div className="flex items-center gap-2 mb-1">
        <MessageSquare className="h-5 w-5 text-[#C17F59]" />
        <h2 className="font-display text-2xl text-[#2D2825]">Contatti da Glowy ({leads.length})</h2>
      </div>
      <p className="text-sm text-[#7A7571] mb-4">Persone interessate ma non ancora pronte, che hanno lasciato nome e WhatsApp in chat. Ricontattale per chiudere la vendita.</p>
      {leads.length === 0 ? (
        <p className="text-sm text-[#B9B4AE]">Ancora nessun contatto raccolto da Glowy.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[#E8E6E1]">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#7A7571] border-b border-[#E8E6E1]">
                <th className="px-4 py-3">Nome</th><th className="px-4 py-3">WhatsApp</th><th className="px-4 py-3">Data</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} data-testid="glowy-lead-row" className="border-b border-[#F0EEE9] last:border-0">
                  <td className="px-4 py-3 font-medium text-[#2D2825]">{l.nome}</td>
                  <td className="px-4 py-3 text-[#2D2825]">{l.whatsapp}</td>
                  <td className="px-4 py-3 text-[#7A7571]">{l.created_at ? new Date(l.created_at).toLocaleString("it-IT") : "—"}</td>
                  <td className="px-4 py-3">
                    <a data-testid="glowy-lead-wa" href={waLink(l.whatsapp)} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-full bg-[#8BA888] px-3 py-1.5 text-xs text-white hover:bg-[#6f8f6c]">
                      <MessageSquare className="h-3.5 w-3.5" /> Scrivi
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SalesPanel = ({ data, onRegister }) => {
  const f = data.funnel || {};
  return (
  <div data-testid="sales-panel" className="mb-8 rounded-3xl bg-[#1f3d33] text-white p-7">
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-2"><ShoppingCart className="h-5 w-5 text-[#8BA888]" /><h2 className="font-display text-2xl">Funnel & Vendite</h2></div>
      <button data-testid="register-webhook" onClick={onRegister} className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 text-sm"><Webhook className="h-4 w-4" /> Registra webhook ordini</button>
    </div>

    {/* Imbuto visivo */}
    <FunnelChart f={f} orders={data.orders} />

    {/* Funnel: analisi -> click carrelli */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div data-testid="funnel-analyses" className="rounded-2xl bg-white/5 p-4"><p className="text-[11px] uppercase tracking-[0.15em] text-white/60">Analisi totali</p><p className="font-display text-3xl">{f.total_analyses ?? 0}</p></div>
      <div data-testid="funnel-clicks-base" className="rounded-2xl bg-white/5 p-4"><p className="text-[11px] uppercase tracking-[0.15em] text-white/60">Click carrello Base</p><p className="font-display text-3xl">{f.clicks_base ?? 0}</p></div>
      <div data-testid="funnel-clicks-premium" className="rounded-2xl bg-white/5 p-4"><p className="text-[11px] uppercase tracking-[0.15em] text-white/60">Click carrello Premium</p><p className="font-display text-3xl">{f.clicks_premium ?? 0}</p></div>
      <div data-testid="funnel-clicks-single" className="rounded-2xl bg-white/5 p-4"><p className="text-[11px] uppercase tracking-[0.15em] text-white/60">Click prodotto singolo</p><p className="font-display text-3xl">{f.clicks_single ?? 0}</p></div>
    </div>

    {/* Vendite reali + CR */}
    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
      <div data-testid="sales-orders" className="rounded-2xl bg-[#8BA888]/15 p-4"><p className="text-[11px] uppercase tracking-[0.15em] text-white/70">Vendite da Glow Score</p><p className="font-display text-3xl">{data.glow_orders ?? data.orders}</p></div>
      <div data-testid="sales-revenue" className="rounded-2xl bg-[#8BA888]/15 p-4"><p className="text-[11px] uppercase tracking-[0.15em] text-white/70">Ricavi da Glow Score</p><p className="font-display text-3xl">€ {data.glow_revenue ?? data.revenue}</p></div>
      <div data-testid="cr-orders-analyses" className="rounded-2xl bg-white/5 p-4"><p className="text-[11px] uppercase tracking-[0.15em] text-white/60">CR vendite / analisi</p><p className="font-display text-3xl">{f.cr_orders_analyses ?? 0}%</p></div>
      <div data-testid="cr-orders-clicks" className="rounded-2xl bg-white/5 p-4"><p className="text-[11px] uppercase tracking-[0.15em] text-white/60">CR vendite / click</p><p className="font-display text-3xl">{f.cr_orders_clicks ?? 0}%</p></div>
    </div>
    {(data.store_orders != null) && (
      <p className="mt-2 text-xs text-white/50" data-testid="store-total">Per riferimento, l'intero store (tutti i canali) ha registrato {data.store_orders} ordini per € {data.store_revenue}. Sopra vedi <strong>solo</strong> le vendite attribuite a un codice sconto generato da Glow Score.</p>
    )}

    {/* Vendite per pacchetto */}
    <PackageSales pkg={data.sales_by_package} f={f} />

    <p className="mt-4 text-xs text-white/60">Le "Vendite da Glow Score" e i relativi ricavi contano <strong>solo</strong> gli ordini reali dello store (via webhook Shopify) che hanno usato un <strong>codice sconto generato da Glow Score</strong>. I click sono le persone che, dopo l'analisi, aprono un carrello. <strong>CR vendite/analisi</strong> = quante analisi diventano acquisti · <strong>CR vendite/click</strong> = quanti click al carrello diventano acquisti. Richiede lo scope <strong>read_orders</strong> sull'app Shopify (aggiungilo e rilascia, poi clicca "Registra webhook ordini").</p>
    {(data.recent || []).length > 0 && (
      <details className="mt-3">
        <summary className="cursor-pointer text-sm text-white/70 hover:text-white">Ultimi ordini ({data.recent.length})</summary>
        <div className="mt-3 space-y-1 text-xs">
          {data.recent.map((o) => (
            <div key={o.id} className="flex justify-between border-t border-white/10 pt-1">
              <span>#{o.order_number || o.id} · {o.email || o.phone || "—"}</span>
              <span className="text-white/70">€ {o.total_price} {o.discount_codes?.length ? `· ${o.discount_codes.join(",")}` : ""}</span>
            </div>
          ))}
        </div>
      </details>
    )}
  </div>
  );
};
